import { createElement, useState, useEffect } from "react"
import { createRoot, Root } from "react-dom/client"

import ReactRailsUJS from "react_ujs"

import Helpers from "@/unpoly/helpers"

export const useInput = ({ ref, enabled = true }) => {
  const changeEventQueue = useDispatchQueue({ dispatch: (e) => UnpolyEmitChangeEvent(e) })

  unpolyHelloRef({ ref, enabled })

  return {
    pauseEvents: changeEventQueue.block,
    playEvents: changeEventQueue.unblock,
    onChange: (event) => {
      ref.current && changeEventQueue.enqueue(Helpers.newChangeEvent(ref.current))
    },
  }
}

// Mark the given input as an unpoly reference
const unpolyHelloRef = ({ ref, enabled }) => {
  useEffect(() => {
    if (!enabled) return
    if (!ref.current) return
    UnpolyElementMounted(ref.current)
  }, [ref])
}

/**
 * React hook for creating a (blockable) DispatchQueue.
 *
 * Upon creation a dispatch function is provided.
 *
 * The dispatch function will be invoked for each element in the queue when the queue is unblocked.
 *
 * If the queue is blocked then elements accumulate until such a time that the queue is unblocked again.
 *
 * Dispatch Queue is useful if you want to enqueue events while a form control is
 * actively focused on and only dispatch when the control loses focus.
 *
 */
const useDispatchQueue = ({ dispatch }) => {
  const [isBlocked, setBlocked] = useState(false)
  const [queue, setQueue] = useState([])

  useEffect(() => {
    if (isBlocked || queue.length === 0) return

    queue.forEach((e) => {
      dispatch(e)
    })

    setQueue([])
  }, [isBlocked, queue])

  return {
    isBlocked,
    block: () => {
      setBlocked(true)
    },
    unblock: () => {
      setBlocked(false)
    },
    enqueue: (e) => {
      setQueue(queue.concat([e]))
    },
  }
}

/**
 * Publishes a change event for the targetElement
 */
const UnpolyEmitChangeEvent = (changeEvent) =>
  Helpers.withUnpoly((up) => {
    up.emit(changeEvent.target, changeEvent)
  })

const UnpolyElementMounted = (element) => Helpers.withUnpoly((up) => up.hello(element))

// The root mounted on each [data-react-class] node, so the node's component
// can be unmounted when Unpoly removes the fragment holding it. react_ujs
// creates a root and discards it, and only unmounts on Turbolinks and pjax
// events, so without this every replaced fragment left its components running
// (SHO-TSK-1543).
const roots = new WeakMap<Element, Root>()

// Mounts the component a react_component node names, as react_ujs would:
// the constructor through its registered require context, the props from its
// JSON attribute.
export const mountReactComponent = (element: Element) => {
  if (roots.has(element)) return

  const className = element.getAttribute(ReactRailsUJS.CLASS_NAME_ATTR)
  const constructor = ReactRailsUJS.getConstructor(className)

  if (!constructor) {
    throw new Error(
      `Cannot find component: '${className}'. Make sure your component is available to render.`
    )
  }

  const propsJson = element.getAttribute(ReactRailsUJS.PROPS_ATTR)
  const root = createRoot(element)

  root.render(createElement(constructor, propsJson && JSON.parse(propsJson)))
  roots.set(element, root)
}

// Deferred a microtask: Unpoly can remove a fragment from inside one of its
// own components (a modal's close handler, an effect), and React will not
// unmount a root synchronously while it is still rendering or committing.
export const unmountReactComponent = (element: Element) => {
  const root = roots.get(element)
  if (!root) return

  roots.delete(element)
  queueMicrotask(() => root.unmount())
}

export const start = ({ up }) => {
  // A compiler rather than react_ujs's own mount handler, because a compiler
  // has a destructor: Unpoly calls it when the fragment is removed. up.boot()
  // compiles the initial page, so this also covers first load.
  up.compiler("[data-react-class]", (element) => {
    mountReactComponent(element)

    return () => unmountReactComponent(element)
  })

  // react_ujs registers its own DOMContentLoaded mount at import; with the
  // compiler above mounting the initial page too, that would mount every node
  // a second time — createRoot() twice per node, and duplicate ActionCable
  // subscriptions wedge the channel resubscribe handshake. Remove it.
  ReactRailsUJS.removeEvent("DOMContentLoaded", ReactRailsUJS.handleMount)
}

export default { start }
